import { ExternalLink, Github, Star, GitFork } from "lucide-react";
import { projectsQuery, siteSettingsQuery } from "@/lib/sanity";
import { useSanityQuery } from "@/hooks/use-sanity-query";
import type { Project, SiteSettings } from "@/types/content";

export default function ProjectsSection() {
  const { data: projects = [] } = useSanityQuery<Project[]>(["sanity", "projects"], projectsQuery);

  const { data: settings } = useSanityQuery<SiteSettings>(["sanity", "siteSettings"], siteSettingsQuery);

  return (
    <section id="projects" className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-card-bg to-dark-bg">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold mb-4 font-mono text-matrix-green">
            {settings?.projectsHeading || "> ls ./projects"}
          </h2>
          <p className="text-xl text-gray-300">
            {settings?.projectsIntro || "Open source contributions and automation tools"}
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {projects.map((project) => (
            <div key={project.id} className="glass-card p-6 rounded-2xl hover:scale-105 transition-transform duration-300">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold text-white font-mono truncate">{project.title}</h3>
                <div className="flex space-x-2">
                  <span className="bg-matrix-green/20 text-matrix-green px-2 py-1 rounded text-xs font-mono flex items-center">
                    <Star className="w-3 h-3 mr-1" />
                    {project.stars ?? 0}
                  </span>
                  <span className="bg-cyber-cyan/20 text-cyber-cyan px-2 py-1 rounded text-xs font-mono flex items-center">
                    <GitFork className="w-3 h-3 mr-1" />
                    {project.forks ?? 0}
                  </span>
                </div>
              </div>
              
              <p className="text-gray-300 mb-4 text-sm">
                {project.description}
              </p>
              
              {project.technologies && project.technologies.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-4">
                  {project.technologies.map((tech, index) => (
                    <span
                      key={index}
                      className="bg-gray-500/20 text-gray-400 px-2 py-1 rounded text-xs font-mono"
                    >
                      {tech}
                    </span>
                  ))}
                </div>
              )}
              
              <div className="flex space-x-4">
                {project.repositoryUrl && (
                  <a
                    href={project.repositoryUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center text-matrix-green hover:text-cyber-cyan transition-colors font-mono text-sm"
                  >
                    <Github className="w-4 h-4 mr-1" />
                    Repository
                  </a>
                )}
                {project.liveUrl && (
                  <a
                    href={project.liveUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center text-cyber-cyan hover:text-matrix-green transition-colors font-mono text-sm"
                  >
                    <ExternalLink className="w-4 h-4 mr-1" />
                    Live Demo
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>

        {projects.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-400 font-mono">No projects found yet.</p>
          </div>
        )}
      </div>
    </section>
  );
}
